import {
  Card,
  CardActionArea,
  Chip,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { MouseEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'usehooks-ts'
import { IconButton, Icons } from 'utoniq-ui'

import {
  EventPreSaleBoxFragment,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { useMessage, useRouter } from '@/core'
import { DateTime } from '@/core/entities'
import { generateFullPath } from '@/lib/url'

type ConsolePreSaleBoxProps = {
  preSale: EventPreSaleBoxFragment
  onTogglePreSalePublish: (preSale: EventPreSaleBoxFragment) => Promise<void>
}

export const ConsolePreSaleBox = ({
  preSale,
  onTogglePreSalePublish,
}: ConsolePreSaleBoxProps) => {
  const { t } = useTranslation('consoleEventDetail')
  const { showMessage } = useMessage()
  const { router, generatePath, routerPages } = useRouter<{ eventId: string }>()

  const [, copy] = useCopyToClipboard()
  const { onClick: onClickBrowserLink } = useBrowserLink(
    generatePath(
      preSale.type === PreSaleType.Lottery
        ? routerPages.LotteryDetail
        : routerPages.FirstComeFirstServedDetail,
      { preSaleId: preSale.id ?? '#' }
    )
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const now = dayjs().unix() * 1000
  const isPublished =
    preSale.isPublished && preSale.startedAt <= now && now <= preSale.endedAt
  // 応募期間・購入期間の表示
  const applicationPeriod = useMemo(() => {
    if (preSale.type === 'lottery') {
      return {
        label: t('applicationPeriod'),
        value: `${DateTime.formatDate(
          preSale.lottery?.applicationStartedAt,
          'YYYY/MM/DD(ddd) HH:mm'
        )} 〜 ${DateTime.formatDate(
          preSale.lottery?.applicationEndedAt,
          'YYYY/MM/DD(ddd) HH:mm'
        )}`,
      }
    }
    return {
      label: t('purchasablePeriod'),
      value: `${DateTime.formatDate(
        preSale.firstComeFirstServed?.purchasableStartedAt,
        'YYYY/MM/DD(ddd) HH:mm'
      )} 〜 ${DateTime.formatDate(
        preSale.firstComeFirstServed?.purchasableEndedAt,
        'YYYY/MM/DD(ddd) HH:mm'
      )}`,
    }
  }, [preSale])

  const options = [
    {
      label: t('applicants'),
      onClick() {
        router.push(
          generatePath(routerPages.ConsoleLotteryManagements, {
            preSaleId: preSale?.id,
          })
        )
      },
      // 応募者管理は抽選の場合のみ表示
      hidden: preSale.type === PreSaleType.FirstComeFirstServed,
    },
    // {
    //   // TODO: 先行販売の公開・非公開を切り替える
    //   label: preSale.isPublished ? t('unpublish') : t('publish'),
    //   onClick() {
    //     onTogglePreSalePublish(preSale)
    //     setAnchorEl(null)
    //   },
    // },
    {
      label: t('copyURLLabel'),
      async onClick() {
        await copy(
          generateFullPath(
            preSale.type === PreSaleType.Lottery
              ? routerPages.LotteryDetail
              : routerPages.FirstComeFirstServedDetail,
            {
              preSaleId: preSale.id ?? '#',
            }
          )
        )
        showMessage(t('copyURLSuccess'), 'success')
        setAnchorEl(null)
      },
    },
    {
      label: t('openPreSaleLabel'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClickBrowserLink()
        setAnchorEl(null)
      },
    },
    {
      label: t('edit'),
      onClick() {
        router.push(
          generatePath(routerPages.ConsolePreSaleEdit, {
            preSaleId: preSale.id,
            eventId: router.query.eventId,
          })
        )
      },
    },
    /**
     * TODO: 先行販売削除のAPI作成後に削除ボタンを実装
     */
    // {
    //   label: t('delete'),
    //   onClick() {},
    // },
  ]

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <Card elevation={0} sx={{ bgcolor: 'transparent', position: 'relative' }}>
      <Stack direction="row" spacing={2}>
        <Stack flex={1}>
          <Stack sx={{ px: 2, pb: 0 }}>
            <Stack
              sx={{ mb: 2 }}
              direction="row"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {/* 抽選・先着の表示 */}
                <Chip
                  sx={{
                    '.MuiChip-root': {
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'grey.500',
                    },
                    fontSize: '10px',
                    fontWeight: 700,
                  }}
                  size="small"
                  label={t(preSale?.type)}
                />
                {/* 公開・非公開の表示 */}
                <Chip
                  size="small"
                  label={t(isPublished ? 'published' : 'unpublished')}
                  color={isPublished ? 'primary' : 'default'}
                />
                {/* 応募ステータスの表示 */}
                <Chip
                  size="small"
                  sx={
                    // Chipに設定されていないカラーなので、独自に設定する
                    preSale.preSaleStatus === 'acceptingLottery'
                      ? {
                          bgcolor: 'info.light',
                          color: 'default',
                          fontSize: '10px',
                          fontWeight: 700,
                        }
                      : preSale.preSaleStatus === 'purchasable'
                      ? {
                          bgcolor: 'secondary.light',
                          color: 'default',
                          fontSize: '10px',
                          fontWeight: 700,
                        }
                      : undefined
                  }
                  label={t(`${preSale.preSaleStatus}`)}
                  color={
                    preSale.preSaleStatus === 'accepting' ||
                    preSale.preSaleStatus === 'beforeReceiption'
                      ? 'secondary'
                      : preSale.preSaleStatus === 'soldOut'
                      ? 'error'
                      : 'default'
                  }
                />
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ pl: 2 }}
                >
                  {t('publishedAt', {
                    date: DateTime.formatDate(
                      String(preSale.formattedStartedAt)
                    ),
                  })}
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="subtitle1">{preSale.title}</Typography>
          </Stack>
          <Stack spacing={0} sx={{ px: 2, py: 2 }}>
            {/* 抽選の場合 応募期間 | 先着の場合 購入期間 */}
            <Typography
              color="text.secondary"
              variant="caption"
              sx={{ fontSize: '10px' }}
            >
              {applicationPeriod.label}
              {applicationPeriod.value}
            </Typography>
            {preSale.type === 'lottery' && (
              <>
                {/* 抽選の場合 当落発表予定日 */}
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ fontSize: '10px' }}
                >
                  {t('resultNoticedAt')}
                  {DateTime.formatDate(
                    preSale.lottery?.resultNoticedAt,
                    'YYYY/MM/DD(ddd) HH:mm'
                  )}
                </Typography>

                {/* 抽選の場合 決済期間 */}
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ fontSize: '10px' }}
                >
                  {t('lotteryPurchasablePeriod')}
                  {DateTime.formatDate(
                    preSale.lottery?.purchasableStartedAt,
                    'YYYY/MM/DD(ddd) HH:mm'
                  )}{' '}
                  〜{' '}
                  {DateTime.formatDate(
                    preSale.lottery?.purchasableEndedAt,
                    'YYYY/MM/DD(ddd) HH:mm'
                  )}
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
        <Stack>
          <IconButton.More
            size="small"
            bgcolor="grey.100"
            color="grey.700"
            onClick={handleClick}
            sx={{
              position: 'relative',
              zIndex: 2,
              transform: 'rotate(90deg)',
            }}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {options.map(
              ({ icon, label, hidden, ...option }) =>
                !hidden && (
                  <MenuItem {...option} sx={{ width: 200 }} key={label}>
                    {icon}
                    {label}
                  </MenuItem>
                )
            )}
          </Menu>
        </Stack>
      </Stack>
      <CardActionArea
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          inset: 0,
          zIndex: 1,
        }}
        href={generatePath(routerPages.ConsolePreSaleDetail, {
          preSaleId: preSale.id ?? '#',
        })}
      />
    </Card>
  )
}
